/* eslint-disable @next/next/no-img-element */
/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { gql } from "@apollo/client";
import { addApolloState, initializeApollo } from "init/apolloClient";
import airbrake from "util/airbrake";
import { formatNumber, isUuid, titleize } from "util/strings";
import { trackPublicSearchStats } from "util/analytics";
import { SQUAREFOOT_FEATURED_LISTINGS_QUERY } from "components/SquarefootFeaturedListings";
import { getAreaSeoData, getAreaSeoRedirect, sanitizeCMS } from "util/seo";
import Head from "next/head";
import getQueryManager, { keysAreDefault } from "context/QueryManager";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import ICESearch from "components/ICESearch";
import Layout from "components/layout";
import { useRouter } from "next/router";
import cn from "classnames";
import Modal from "components/componentLibrary/Modals/Modal";
import { ES_LISTING_SEARCH_QUERY } from "components/ClientSearch/Listings";
import { MARKET_QUERY } from "components/ICESearch/SEOFooter";
import styles from "./styles.module.scss";

const AREA_QUERY_WITH_CMS_CONTENT = gql`
  query areaQueryPublicIceSearch($externalId: ID!) {
    area(externalId: $externalId) {
      id
      externalId
      parameterizedName
      name
      seoString
      ... on Neighborhood {
        geojson
        visibility
        market {
          id
          name
          parameterizedName
          externalId
          seoString
          position {
            lat
            lng
          }
          neighborhoods(visibility: [VISIBLE, HIDDEN]) {
            id
            name
            geojson
            externalId
            parameterizedName
            seoString
            visibility
          }
        }
        submarketsOfNhood: submarkets {
          id
          name
          parameterizedName
          seoString
          externalId
        }
        cmsContent {
          id: title
          title
          content
          faq {
            question
            answer
          }
        }
        __typename
      }
      ... on Market {
        name
        submarkets {
          id
          externalId
          seoString
          name
          parameterizedName
          geojson
          visibility
          neighborhoods(visibility: [VISIBLE, HIDDEN]) {
            id
            name
            geojson
            externalId
            parameterizedName
            seoString
            visibility
          }
        }
        position {
          lat
          lng
        }
        seoString
        neighborhoods(visibility: [VISIBLE, HIDDEN]) {
          id
          name
          geojson
          externalId
          parameterizedName
          seoString
          visibility
        }
        cmsContent {
          id: title
          title
          content
          faq {
            question
            answer
          }
        }
        __typename
      }
      ... on Submarket {
        geojson
        visibility
        market {
          id
          name
          externalId
          parameterizedName
          seoString
          position {
            lat
            lng
          }
        }
        neighborhoods(visibility: [VISIBLE, HIDDEN]) {
          id
          name
          geojson
          externalId
          parameterizedName
          seoString
          visibility
        }
        cmsContent {
          id: title
          title
          content
          faq {
            question
            answer
          }
        }
        __typename
      }
    }
  }
`;

function getTitle(area, market) {
  // this function is for testing some seo theories. https://linear.app/squarefoot/issue/SQRFT-736/update-page-title-for-la-and-ny
  const areaTitles = {
    "new-york": "New York City, NY",
    "los-angeles": "Los Angeles, CA",
  };
  if (area !== market && areaTitles[market])
    return `${titleize(area)}, ${areaTitles[market]}`;
  return areaTitles[market] || titleize(area);
}

function getMetaDescription(area, market, count) {
  // this function is for testing some seo theories. https://linear.app/squarefoot/issue/SQRFT-737/update-meta-description-for-la
  const areaDescriptions = {
    "new-york": `Browse ${
      count ? formatNumber(count) : ""
    } available office spaces for rent in ${
      area !== market ? `${titleize(area)},` : ""
    } New York City, NY. Find the perfect office rental for your business with SquareFoot.`,
    "los-angeles": `Browse ${
      count ? formatNumber(count) : ""
    } available office spaces for rent in ${
      area !== market ? `${titleize(area)},` : ""
    } Los Angeles, CA. Find the perfect office rental for your business with SquareFoot.`,
  };
  const defaultDescription = `Explore ${
    count ? formatNumber(count) : ""
  } office space listings in ${titleize(
    area
  )}. Find the perfect space for your business at SquareFoot.`;

  return areaDescriptions[market] || defaultDescription;
}

export default function MarketICEPublicSearch({
  queryManager,
  sanitizedCMS,
  externalId,
  area,
  market,
}) {
  const router = useRouter();
  const [values, setValues] = useState(queryManager.values);
  useEffect(() => {
    trackPublicSearchStats(queryManager.values);
    setValues(queryManager.values);
  }, [queryManager.values]);
  const changeHandler = (query) =>
    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined
    );
  const { update } = useContext(QueryManagerContext);
  return (
    <div>
      <Head>
        <title>
          {getTitle(area, market?.parameterizedName)} Office Space for Rent -
          SquareFoot{values.page > 1 ? ` - page ${values.page}` : ""}
        </title>
        <meta
          name="description"
          content={getMetaDescription(
            area,
            market?.parameterizedName,
            values.count
          )}
        />
      </Head>
      {values.promo === "coworking" && (
        <Modal
          onClose={() => {
            update({ promo: "" }, values, setValues, changeHandler);
          }}
        >
          <div className={styles.promoThanks}>
            <h1 className={styles.promoTitle}>
              Thank you for redeeming your offer! A representative will be in
              touch shortly.
            </h1>
            <div className={styles.promoBody}>
              In the meantime, begin your search by viewing listings.
            </div>
            <button
              className={cn("btn-primary")}
              type="button"
              onClick={() => {
                update({ promo: "" }, values, setValues, changeHandler);
              }}
              data-testid="coworkingEmailPromoSubmitButton"
            >
              VIEW LISTINGS
            </button>
          </div>
        </Modal>
      )}
      <QueryManagerContext.Provider
        value={{
          values,
          update: (newValues) =>
            update(newValues, values, setValues, changeHandler),
          keysAreDefault: (keys) => keysAreDefault(keys, values),
          defaultValues: queryManager.defaultValues,
          getSearchFromActiveRequirements: () => {},
        }}
      >
        <Layout>
          <ICESearch sanitizedCMS={sanitizedCMS} externalId={externalId} />
        </Layout>
      </QueryManagerContext.Provider>
    </div>
  );
}

MarketICEPublicSearch.propTypes = {
  queryManager: PropTypes.shape({
    values: PropTypes.shape({}),
    defaultValues: PropTypes.shape({}),
  }).isRequired,
  sanitizedCMS: PropTypes.shape({
    __typename: PropTypes.string,
    faq: PropTypes.arrayOf(PropTypes.shape({})),
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  externalId: PropTypes.string,
  area: PropTypes.string.isRequired,
  market: PropTypes.shape({
    parameterizedName: PropTypes.string.isRequired,
  }),
};
MarketICEPublicSearch.defaultProps = {
  sanitizedCMS: null,
  externalId: null,
  market: null,
};

export async function getServerSideProps({ query, params, resolvedUrl, req }) {
  if (!params?.slug) return { notFound: true };
  const { slug } = params;
  const externalId = slug[slug.length - 1];

  if (!isUuid(externalId)) return { notFound: true };

  const apolloClient = initializeApollo({ req });

  // -- area seo redirect ---
  const areaSeoData = await getAreaSeoData(apolloClient, externalId);

  if (!areaSeoData || !areaSeoData.area) return { notFound: true }; // after this point areaSeoData is always defined

  const redirect = getAreaSeoRedirect(areaSeoData, resolvedUrl);
  if (redirect) return redirect;
  // --- end area seo redirect ---

  // // --- start building query variables ---
  const queryManager = getQueryManager({
    queryObj: query,
  });
  const { values } = queryManager;

  const areaExternalIds = [
    ...values.neighborhoods,
    ...values.submarkets,
  ].filter(isUuid); // filter allows only uuid values

  if (areaExternalIds.length === 0) {
    switch (areaSeoData.area.__typename) {
      case "Neighborhood":
        queryManager.values.neighborhoods = [externalId];
        areaExternalIds.push(externalId);
        break;
      case "Submarket":
        queryManager.values.submarkets = [externalId];
        areaExternalIds.push(externalId);
        break;

      case "Market":
        queryManager.values.market = areaSeoData.area;
        areaExternalIds.push(areaSeoData.area.externalId);
        break;

      default:
        airbrake.notify(
          `MarketPublicSearchPage -> getServerSideProps -> AREA_QUERY_WITH_SEO_STRING :
            Expected typename to be "Neighborhood", "Market", "City", or "Submarket" but
            received "${areaSeoData.area.__typename}"; query was made with externalId=${externalId}`
        );
        break;
    }
  }

  const variables = {
    groupByBuilding: values.groupByBuilding,
    maxDaysOld: values.maxDaysOld,
    minSqft: values.minSqft,
    maxSqft: values.maxSqft,
    minOccupancy: values.minOccupancy,
    maxOccupancy: values.maxOccupancy,
    leaseTermMin: values.leaseTermMin,
    leaseTermMax: values.leaseTermMax,
    minMonthlyPrice: values.minMonthlyPrice,
    maxMonthlyPrice: values.maxMonthlyPrice,
    minPricePerSquareFoot: values.minPricePerSquareFoot,
    maxPricePerSquareFoot: values.maxPricePerSquareFoot,
    leaseTypes: values.leaseTypes,
    areaExternalIds,
    first: values.limit,
    after: ((values.page - 1) * values.limit).toString(),
  };

  if (values.activePriceFilter === "MONTHLY") {
    delete variables.minPricePerSquareFoot;
    delete variables.maxPricePerSquareFoot;
  } else if (values.activePriceFilter === "PSF") {
    delete variables.minMonthlyPrice;
    delete variables.maxMonthlyPrice;
  }
  if (values.activeSizeFilter === "SEATS") {
    delete variables.minSqft;
    delete variables.maxSqft;
  } else if (values.activeSizeFilter === "SQUARE_FEET") {
    delete variables.minOccupancy;
    delete variables.maxOccupancy;
  }
  // --- perform queries ---
  const [
    { data: esListingSearchData, error: publicListingSearchError },
    { data: areaQueryWithCmsData, error: areaQueryWithCmsError },
  ] = await Promise.all([
    apolloClient.query({
      query: ES_LISTING_SEARCH_QUERY,
      variables,
    }),
    apolloClient.query({
      query: AREA_QUERY_WITH_CMS_CONTENT,
      variables: {
        externalId,
      },
    }),
  ]);

  if (publicListingSearchError) throw publicListingSearchError;
  if (areaQueryWithCmsError) throw areaQueryWithCmsError;
  // --- end perform queries ---

  // if code reaches this point then area will not be null, area seo query confirms that.
  // graphql schema is nullable though to ts will complain if ! is excluded.
  const area = areaQueryWithCmsData?.area;

  const { __typename: areaType } = area;
  const { listings } = esListingSearchData?.publicListingNiceSearch;

  // --- get additional area information from uuids ---
  let market = null;
  let embellishedNeighborhoods = [];
  let embellishedSubmarkets = [];
  const areasOfInterest = [
    ...queryManager.values.neighborhoods,
    ...queryManager.values.submarkets,
  ];
  if (areaType === "Market") {
    market = area;
    areasOfInterest?.forEach((n) => {
      const embellishedNeighborhood = area.neighborhoods.find(
        (nhood) => n === nhood?.externalId
      );
      if (embellishedNeighborhood)
        embellishedNeighborhoods.push(embellishedNeighborhood);
    });
    embellishedSubmarkets = area.submarkets.filter((s) => {
      return s && areasOfInterest?.includes(s.externalId);
    });
  } else if (areaType === "Submarket") {
    market = area.market;
    areasOfInterest?.forEach((n) => {
      const embellishedNeighborhood = area.neighborhoods.find(
        (nhood) => n === nhood?.externalId
      );

      if (embellishedNeighborhood)
        embellishedNeighborhoods.push(embellishedNeighborhood);
    });
    embellishedSubmarkets = [area];
  } else if (areaType === "Neighborhood") {
    market = area.market;
    embellishedNeighborhoods = [area];
  }
  //  --- end get additional area information from area uuids ---

  const sanitizedCMS =
    values.page === 1 &&
    values.groupByBuilding &&
    areaType !== "City" &&
    area.cmsContent
      ? sanitizeCMS(area.cmsContent)
      : null;

  // prime apollo cache with featured listings
  await Promise.all([
    apolloClient.query({
      query: SQUAREFOOT_FEATURED_LISTINGS_QUERY,
      variables: {
        neighborhoods:
          embellishedNeighborhoods
            .map(({ parameterizedName }) => parameterizedName)
            .join(", ") || "",
        areaUuid: area.externalId,
        live: true,
      },
    }),
  ]);

  if (market)
    await apolloClient.query({
      query: MARKET_QUERY,
      variables: { marketUuid: market?.externalId },
    });

  queryManager.values.listings = listings.nodes;
  queryManager.values.count = listings.pageInfo.count;
  queryManager.values.neighborhoods = embellishedNeighborhoods;
  queryManager.values.submarkets = embellishedSubmarkets;
  queryManager.values.market = market;
  queryManager.values.area = externalId;

  return addApolloState(apolloClient, {
    props: {
      queryManager,
      sanitizedCMS,
      externalId,
      area: area.parameterizedName,
      market,
    },
  });
}
